/**
 * Spec 69 — Conversation Intent Layer
 *
 * Wire-format types for the classifier output, the persisted decision
 * payload, and the dispatch outcome. Owned in shared so server, engine,
 * and client all reference the same structure.
 */
import { z } from 'zod';

// ============================================================================
// Intent kind — mirrors the `conversation_intent_kind` PG enum exactly.
// Keep this list in sync with packages/shared/src/db/schema/enums.ts.
// ============================================================================

export const ConversationIntentKindSchema = z.enum([
  'direct_answer',
  'workflow',
  'task',
  'project',
  'marketplace',
  'ambiguous',
  'template',
  'workforce',
]);

export type ConversationIntentKind = z.infer<typeof ConversationIntentKindSchema>;

export const CONVERSATION_INTENT_KINDS: ReadonlyArray<ConversationIntentKind> = ConversationIntentKindSchema.options;

// ============================================================================
// Per-kind seed payload — what the downstream drafter consumes so it does
// not re-classify. Shape grows as Specs 70-73 mature; today only the
// fields the classifier reliably produces are required.
// ============================================================================

const DirectAnswerSeed = z.object({
  kind: z.literal('direct_answer'),
  /** True when the user explicitly said "direct answer" in their message. */
  userStatedKind: z.boolean().optional(),
});

const WorkflowSeed = z.object({
  kind: z.literal('workflow'),
  suggestedSchedule: z.string().max(200).optional(),
  suggestedSteps: z.array(z.string().max(500)).max(20).optional(),
  /** True when the user explicitly used the word "workflow" in their message. */
  userStatedKind: z.boolean().optional(),
});

const TaskSeed = z.object({
  kind: z.literal('task'),
  suggestedTemplateSlug: z.string().max(200).optional(),
  budgetHint: z
    .object({
      tokens: z.number().int().positive().optional(),
      usd: z.number().nonnegative().optional(),
      deadlineHours: z.number().nonnegative().optional(),
    })
    .optional(),
  /** True when the user explicitly used the word "task" in their message. */
  userStatedKind: z.boolean().optional(),
});

const ProjectSeed = z.object({
  kind: z.literal('project'),
  suggestedName: z.string().max(200).optional(),
  suggestedWorkstreams: z.array(z.string().max(200)).max(10).optional(),
  suggestedTemplates: z.array(z.string().max(200)).max(20).optional(),
  /** True when the user explicitly used the word "project" in their message. */
  userStatedKind: z.boolean().optional(),
});

const MarketplaceSeed = z.object({
  kind: z.literal('marketplace'),
  query: z.string().max(500),
  suggestedTemplateIds: z.array(z.string().max(200)).max(20).optional(),
  /** True when the user explicitly asked for marketplace/community content. */
  userStatedKind: z.boolean().optional(),
});

const AmbiguousSeed = z.object({
  kind: z.literal('ambiguous'),
  /** Question rendered to the user above the disambiguation pills. */
  clarificationPrompt: z.string().min(1).max(500),
});

/**
 * Seed for the template drafter (Spec 79 M1.5).
 * The drafter searches existing agent_templates first (find-first); only if
 * no match scores ≥ threshold does it fall through to a "Create new template" card.
 */
const TemplateSeed = z.object({
  kind: z.literal('template'),
  /** Free-text description of what the template should do. */
  query: z.string().max(500),
  /** Classifier-suggested department slug (e.g. 'research', 'risk'). */
  suggestedDepartment: z.string().max(120).optional(),
  /** True when the user explicitly used the word "template" in their message. */
  userStatedKind: z.boolean().optional(),
});

/**
 * Seed for the workforce drafter (Spec 79 M1.5).
 * The drafter searches existing daemons first; if none match, it falls back to
 * searching templates that could be hired into a daemon.
 */
const WorkforceSeed = z.object({
  kind: z.literal('workforce'),
  /** Free-text description of the agent mandate / role needed. */
  query: z.string().max(500),
  /** Classifier-suggested template slug to use when hiring a new daemon. */
  suggestedTemplateSlug: z.string().max(200).optional(),
  /** True when the user explicitly used "workforce", "employee", or "hire" in their message. */
  userStatedKind: z.boolean().optional(),
});

export const IntentSeedSchema = z.discriminatedUnion('kind', [
  DirectAnswerSeed,
  WorkflowSeed,
  TaskSeed,
  ProjectSeed,
  MarketplaceSeed,
  AmbiguousSeed,
  TemplateSeed,
  WorkforceSeed,
]);

export type IntentSeed = z.infer<typeof IntentSeedSchema>;

// ============================================================================
// IntentClassification — the classifier's structured output. Persisted
// inside IntentClassificationDecisionPayload below.
// ============================================================================

export const IntentClassificationSchema = z
  .object({
    kind: ConversationIntentKindSchema,
    confidence: z.number().min(0).max(1),
    rationale: z.string().min(1).max(500),

    /**
     * Up to 3 alternatives the classifier considered, in score order.
     * Drives the disambiguation UI when `kind === 'ambiguous'`.
     */
    alternatives: z
      .array(
        z.object({
          kind: ConversationIntentKindSchema,
          confidence: z.number().min(0).max(1),
          suggestedLabel: z.string().min(1).max(120),
        }),
      )
      .max(3),

    seed: IntentSeedSchema,
  })
  .superRefine((c, ctx) => {
    if (c.seed.kind !== c.kind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['seed', 'kind'],
        message: `seed.kind ('${c.seed.kind}') must equal kind ('${c.kind}')`,
      });
    }
  });

export type IntentClassification = z.infer<typeof IntentClassificationSchema>;

// ============================================================================
// IntentClassificationDecisionPayload — the JSONB payload stored in
// `decisions.payload` when decisionType === 'intent_classified'.
// ============================================================================

export const INTENT_DECISION_TYPE = 'intent_classified' as const;

export const IntentClassificationDecisionPayloadSchema = z.object({
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  classification: IntentClassificationSchema,

  /** SHA-256 of (message content + summary + recentMessages). Used by the
   *  idempotency check — re-classifying the same input returns the prior
   *  decision row instead of issuing another LLM call. */
  inputHash: z.string().length(64),

  /** When this row is the result of a user picking a pill from a prior
   *  ambiguous result, this is the prior decision row's id. Else null. */
  disambiguatedFromDecisionId: z.number().int().positive().nullable(),

  /** Provider:model identifier, e.g. 'anthropic:claude-haiku-4-5-20251001'. */
  modelStamp: z.string().min(1).max(200),

  /** Wall-clock ms the classifier took. */
  latencyMs: z.number().nonnegative(),
});

export type IntentClassificationDecisionPayload = z.infer<typeof IntentClassificationDecisionPayloadSchema>;

/** Convenience alias — the workflow-kind slice of IntentSeed. */
export type IntentSeedWorkflow = Extract<IntentSeed, { kind: 'workflow' }>;

/** Convenience alias — the template-kind slice of IntentSeed. */
export type IntentSeedTemplate = Extract<IntentSeed, { kind: 'template' }>;

/** Convenience alias — the workforce-kind slice of IntentSeed. */
export type IntentSeedWorkforce = Extract<IntentSeed, { kind: 'workforce' }>;

// ============================================================================
// IntentDrafterToolCallContent — the shape of a conversation_messages row
// with role='tool' written by each drafter as an in-conversation audit trail.
//
// The message id is the panel handle: ?panel=search-results:<messageId>
// Frontend parses conversation_messages.content as this shape when role='tool'.
// ============================================================================

/**
 * A single search result item returned by the drafter's entity search.
 * `score` is normalized to [0, 1] where possible (raw RRF scores are
 * normalized by dividing by the max score in the result set).
 */
export const IntentDrafterToolResultItemSchema = z.object({
  id: z.number().int().positive(),
  kind: z.union([z.literal('workflow_definition'), z.literal('agent_template'), z.literal('daemon')]),
  name: z.string(),
  description: z.string().optional(),
  score: z.number().min(0).max(1),
  source: z.union([z.literal('local'), z.literal('builtin'), z.literal('marketplace')]),
});

export type IntentDrafterToolResultItem = z.infer<typeof IntentDrafterToolResultItemSchema>;

export const IntentDrafterToolCallInputSchema = z.object({
  entityType: z.union([z.literal('workflow'), z.literal('template'), z.literal('employee'), z.literal('cross-entity')]),
  query: z.string(),
  department: z.string().optional(),
  limit: z.number().int().positive().optional(),
});

export type IntentDrafterToolCallInput = z.infer<typeof IntentDrafterToolCallInputSchema>;

export const IntentDrafterToolCallOutputSchema = z.object({
  results: z.array(IntentDrafterToolResultItemSchema),
  summary: z.object({
    topMatch: z
      .object({
        id: z.number().int().positive(),
        kind: z.string(),
        name: z.string(),
        score: z.number().min(0).max(1),
      })
      .optional(),
    confidence: z.union([z.literal('high'), z.literal('medium'), z.literal('low'), z.literal('none')]),
  }),
});

export type IntentDrafterToolCallOutput = z.infer<typeof IntentDrafterToolCallOutputSchema>;

/**
 * The full content shape of a conversation_messages row with role='tool'
 * written by the intent drafters for in-conversation observability.
 *
 * Stored as JSON in conversation_messages.content.
 * Panel link contract: ?panel=search-results:<conversationMessageId>
 */
export const IntentDrafterToolCallContentSchema = z.object({
  toolCall: z.object({
    name: z.literal('find-entity-by-description'),
    input: IntentDrafterToolCallInputSchema,
    output: IntentDrafterToolCallOutputSchema,
    /** Right-panel URL: ?panel=search-results:<conversationMessageId> */
    panelLink: z.string(),
    drafterChose: z.union([z.literal('advised'), z.literal('drafted'), z.literal('stub')]),
    /** Trace id — reuses the conversation message id for M1.5. */
    traceId: z.string(),
  }),
});

export type IntentDrafterToolCallContent = z.infer<typeof IntentDrafterToolCallContentSchema>;

// ============================================================================
// DispatchOutcome — the routing result returned by the service. Stays
// narrow on purpose; downstream specs (70-73) widen the union as their
// drafters land.
// ============================================================================

export type DispatchOutcome =
  | { dispatched: 'direct_answer' }
  | { dispatched: 'workflow'; workflowDraftId: number }
  | { dispatched: 'task'; taskId?: number; advisedMessageId?: number }
  | { dispatched: 'project'; projectId: number; workstreamId: number }
  | { dispatched: 'marketplace'; advisedMessageId: number }
  | { dispatched: 'ambiguous'; disambiguationMessageId: number }
  | { dispatched: 'template'; advisedMessageId: number; advisedEntityId?: number }
  | { dispatched: 'workforce'; advisedMessageId: number; advisedEntityId?: number };
